import React, { useState, useEffect } from 'react';
import { getBusinessSettings } from '../utils/businessSettings';

interface PrivacyAmountProps {
  amount?: number | string;
  prefix?: string;
  suffix?: string;
  isPrivacyMode?: boolean;
  className?: string;
  formattedText?: string;
}

/**
 * PrivacyAmount masks financial numbers with a blur effect when Privacy Mode is enabled.
 * Allows quick hover-to-peak for the user while protecting their screen in public spaces.
 * Automatically adapts to the active currency symbol configured in Business Settings.
 */
export function PrivacyAmount({
  amount,
  prefix,
  suffix = '',
  isPrivacyMode = false,
  className = '',
  formattedText
}: PrivacyAmountProps) {
  const [activeCurrencySymbol, setActiveCurrencySymbol] = useState<string>(() => {
    return getBusinessSettings().currencySymbol || 'R';
  });

  useEffect(() => {
    const handleSettingsUpdate = (e: CustomEvent) => {
      if (e.detail?.currencySymbol) {
        setActiveCurrencySymbol(e.detail.currencySymbol);
      }
    };

    window.addEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    return () => {
      window.removeEventListener('businessSettingsUpdated', handleSettingsUpdate as EventListener);
    };
  }, []);

  const resolvedPrefix = prefix !== undefined ? prefix : activeCurrencySymbol;

  let displayStr = '';
  if (formattedText !== undefined) {
    displayStr = formattedText;
  } else if (typeof amount === 'number') {
    displayStr = `${resolvedPrefix}${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${suffix}`;
  } else if (amount !== undefined) {
    displayStr = `${resolvedPrefix}${amount}${suffix}`;
  }

  if (isPrivacyMode) {
    return (
      <span
        className={`inline-block select-none blur-[6px] hover:blur-[1px] transition-all duration-200 cursor-pointer ${className}`}
        title="Privacy Mode Active • Hover to briefly peak"
        aria-label="Sensitive figure blurred for privacy"
      >
        {displayStr || `${resolvedPrefix}0`}
      </span>
    );
  }

  return <span className={className}>{displayStr}</span>;
}

export function PrivacyBlur({
  children,
  isPrivacyMode = false,
  className = ''
}: {
  children: React.ReactNode;
  isPrivacyMode?: boolean;
  className?: string;
}) {
  if (isPrivacyMode) {
    return (
      <span
        className={`inline-block select-none blur-[6px] hover:blur-[1px] transition-all duration-200 cursor-pointer ${className}`}
        title="Privacy Mode Active • Hover to briefly peak"
      >
        {children}
      </span>
    );
  }
  return <span className={className}>{children}</span>;
}
